import API_BASE_URL from '../config/api';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Fade,
  Backdrop,
  CircularProgress,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Paper
} from '@mui/material';
import {
  Close,
  Email,
  Lock,
  Person,
  Phone,
  Visibility,
  VisibilityOff,
  LocationOn,
  Flag,
  Badge,
  Upload,
  PhotoCamera,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';

// Complete list of countries sorted alphabetically
const countries = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo',
  'Costa Rica',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'East Timor',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Ivory Coast',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kosovo',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Non Specified - Bedoun',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe'
];

// Gender options
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

const AuthModal = ({ open, onClose, language, t }) => {
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Registration step state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Personal Information', 'Account & Verification'];

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  // Register form state - country defaults to Kuwait
  const [registerData, setRegisterData] = useState({
    firstName: '',
    middleName: '',
    familyName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    country: 'Kuwait',
    nationality: '',
    civilId: '',
    gender: '',
    civilIdFront: null,
    civilIdBack: null,
    agreeTerms: false
  });

  // Image preview states
  const [civilIdFrontPreview, setCivilIdFrontPreview] = useState(null);
  const [civilIdBackPreview, setCivilIdBackPreview] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    setShowForgotPassword(false);
    setActiveStep(0);
  };

  // Handle image file selection
  const handleImageChange = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === 'civilIdFront') {
          setCivilIdFrontPreview(reader.result);
          setRegisterData(prev => ({ ...prev, civilIdFront: file }));
        } else if (field === 'civilIdBack') {
          setCivilIdBackPreview(reader.result);
          setRegisterData(prev => ({ ...prev, civilIdBack: file }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Login successful!');
        
        if (typeof(Storage) !== 'undefined') {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } else {
        if (response.status === 401) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (response.status === 423) {
          setError('Your account has been temporarily locked due to multiple failed login attempts. Please try again later.');
        } else if (response.status === 404) {
          setError('No account found with this email address. Please register first.');
        } else {
          setError(data.error || 'Login failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotPasswordEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password reset link has been sent to your email. Please check your inbox.');
        setForgotPasswordEmail('');
        setTimeout(() => {
          setShowForgotPassword(false);
        }, 3000);
      } else {
        setError(data.error || 'Failed to send reset link. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Validate Step 1
  const validateStep1 = () => {
    if (!registerData.firstName) {
      setError('First name is required');
      return false;
    }
    if (!registerData.familyName) {
      setError('Family name is required');
      return false;
    }
    if (!registerData.email) {
      setError('Email is required');
      return false;
    }
    if (!registerData.phone) {
      setError('Phone number is required');
      return false;
    }
    if (!registerData.nationality) {
      setError('Nationality is required');
      return false;
    }
    if (!registerData.gender) {
      setError('Gender is required');
      return false;
    }
    if (!registerData.civilId) {
      setError('Civil ID is required');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Enhanced validation
    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!registerData.agreeTerms) {
      setError('Please agree to the terms and conditions');
      setLoading(false);
      return;
    }

    if (registerData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!registerData.civilIdFront) {
      setError('Civil ID front image is required');
      setLoading(false);
      return;
    }

    if (!registerData.civilIdBack) {
      setError('Civil ID back image is required');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('firstName', registerData.firstName);
      formData.append('middleName', registerData.middleName);
      formData.append('familyName', registerData.familyName);
      formData.append('email', registerData.email);
      formData.append('phone', registerData.phone);
      formData.append('password', registerData.password);
      formData.append('country', registerData.country);
      formData.append('nationality', registerData.nationality);
      formData.append('civilId', registerData.civilId);
      formData.append('gender', registerData.gender);
      formData.append('civilIdFront', registerData.civilIdFront);
      formData.append('civilIdBack', registerData.civilIdBack);

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Registration successful! Please check your email for verification.');
        
        if (typeof(Storage) !== 'undefined') {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } else {
        if (response.status === 400) {
          if (data.error && data.error.includes('User already exists')) {
            setError('An account with this email address already exists. Please login instead.');
          } else if (data.error && data.error.includes('Civil ID already registered')) {
            setError('This Civil ID is already registered. Please contact support if this is an error.');
          } else if (data.error && data.error.includes('Both civil ID')) {
            setError('Please upload both front and back images of your Civil ID.');
          } else {
            setError(data.error || 'Registration failed. Please check all fields and try again.');
          }
        } else if (response.status === 500) {
          setError('Server error. Please try again later or contact support if the problem persists.');
        } else {
          setError(data.error || 'Registration failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (form, field, value) => {
    if (form === 'login') {
      setLoginData(prev => ({ ...prev, [field]: value }));
    } else {
      setRegisterData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '95vh',
          overflow: 'auto'
        }
      }}
    >
      <DialogContent>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: -8,
              top: -8,
              color: 'grey.500'
            }}
          >
            <Close />
          </IconButton>

          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: 1,
              fontWeight: 600,
              background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {t?.welcome || 'Welcome'}
          </Typography>

          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {tabValue === 0 
              ? (showForgotPassword ? 'Reset your password' : t?.loginSubtitle || 'Sign in to your account')
              : t?.registerSubtitle || 'Create a new account'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {!showForgotPassword && (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 3 }}
            >
              <Tab label={t?.signIn || 'Sign In'} />
              <Tab label={t?.register || 'Register'} />
            </Tabs>
          )}

          {tabValue === 0 ? (
            showForgotPassword ? (
              // Forgot Password Form
              <Box component="form" onSubmit={handleForgotPassword}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Enter your email address and we'll send you a link to reset your password.
                </Typography>

                <TextField
                  fullWidth
                  label={t?.emailLabel || 'Email'}
                  type="email"
                  required
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FFA500 30%, #FFD700 90%)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setShowForgotPassword(false)}
                  sx={{ mt: 2 }}
                >
                  Back to Login
                </Button>
              </Box>
            ) : (
              // Login Form
              <Box component="form" onSubmit={handleLogin}>
                <TextField
                  fullWidth
                  label={t?.emailLabel || 'Email'}
                  type="email"
                  required
                  value={loginData.email}
                  onChange={(e) => handleInputChange('login', 'email', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label={t?.password || 'Password'}
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={loginData.password}
                  onChange={(e) => handleInputChange('login', 'password', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={loginData.rememberMe}
                        onChange={(e) => handleInputChange('login', 'rememberMe', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t?.rememberMe || 'Remember me'}
                  />
                  
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setShowForgotPassword(true)}
                    sx={{ cursor: 'pointer' }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FFA500 30%, #FFD700 90%)',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : (t?.signIn || 'Sign In')}
                </Button>
              </Box>
            )
          ) : (
            // 2-Step Registration Form
            <Box>
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box component="form" onSubmit={handleRegister}>
                {activeStep === 0 ? (
                  // Step 1: Personal Information
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`${t?.firstName || 'First Name'} *`}
                        required
                        value={registerData.firstName}
                        onChange={(e) => handleInputChange('register', 'firstName', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t?.middleName || 'Middle Name'}
                        value={registerData.middleName}
                        onChange={(e) => handleInputChange('register', 'middleName', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`${t?.familyName || 'Family Name'} *`}
                        required
                        value={registerData.familyName}
                        onChange={(e) => handleInputChange('register', 'familyName', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={`${t?.emailLabel || 'Email'} *`}
                        type="email"
                        required
                        value={registerData.email}
                        onChange={(e) => handleInputChange('register', 'email', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${t?.phoneLabel || 'Phone Number'} *`}
                        required
                        value={registerData.phone}
                        onChange={(e) => handleInputChange('register', 'phone', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${t?.civilId || 'Civil ID'} *`}
                        required
                        value={registerData.civilId}
                        onChange={(e) => handleInputChange('register', 'civilId', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Badge />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        options={countries}
                        value={registerData.nationality}
                        onChange={(event, newValue) => {
                          handleInputChange('register', 'nationality', newValue || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${t?.nationality || 'Nationality'} *`}
                            required
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <Flag />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        options={genderOptions}
                        getOptionLabel={(option) => option.label}
                        value={genderOptions.find(opt => opt.value === registerData.gender) || null}
                        onChange={(event, newValue) => {
                          handleInputChange('register', 'gender', newValue?.value || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${t?.gender || 'Gender'} *`}
                            required
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  // Step 2: Account & Verification
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t?.civilIdFront || 'Civil ID Front'} *
                      </Typography>
                      <input
                        accept="image/*"
                        type="file"
                        id="civilIdFront"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageChange('civilIdFront', e.target.files[0])}
                      />
                      <label htmlFor="civilIdFront">
                        <Button
                          variant="outlined"
                          component="span"
                          fullWidth
                          startIcon={<PhotoCamera />}
                          size="small"
                        >
                          {t?.uploadImage || 'Upload Image'}
                        </Button>
                      </label>
                      {civilIdFrontPreview && (
                        <Box sx={{ mt: 1 }}>
                          <img
                            src={civilIdFrontPreview}
                            alt="Civil ID Front"
                            style={{ width: '100%', borderRadius: 8 }}
                          />
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t?.civilIdBack || 'Civil ID Back'} *
                      </Typography>
                      <input
                        accept="image/*"
                        type="file"
                        id="civilIdBack"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageChange('civilIdBack', e.target.files[0])}
                      />
                      <label htmlFor="civilIdBack">
                        <Button
                          variant="outlined"
                          component="span"
                          fullWidth
                          startIcon={<PhotoCamera />}
                          size="small"
                        >
                          {t?.uploadImage || 'Upload Image'}
                        </Button>
                      </label>
                      {civilIdBackPreview && (
                        <Box sx={{ mt: 1 }}>
                          <img
                            src={civilIdBackPreview}
                            alt="Civil ID Back"
                            style={{ width: '100%', borderRadius: 8 }}
                          />
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${t?.password || 'Password'} *`}
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={registerData.password}
                        onChange={(e) => handleInputChange('register', 'password', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${t?.confirmPassword || 'Confirm Password'} *`}
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={registerData.confirmPassword}
                        onChange={(e) => handleInputChange('register', 'confirmPassword', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        {t?.passwordNote || 'Password must be at least 6 characters long'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={registerData.agreeTerms}
                            onChange={(e) => handleInputChange('register', 'agreeTerms', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {t?.agreeTermsTextPrefix || 'I agree to the'}{' '}
                            <Link href="#" underline="hover">
                              {t?.termsOfService || 'Terms of Service'}
                            </Link>
                            {' '}{t?.and || 'and'}{' '}
                            <Link href="#" underline="hover">
                              {t?.privacyPolicy || 'Privacy Policy'}
                            </Link>
                          </Typography>
                        }
                        sx={{ mt: 1, mb: 1 }}
                      />
                    </Grid>
                  </Grid>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  {activeStep === 0 ? (
                    <Box sx={{ flex: 1 }} />
                  ) : (
                    <Button
                      startIcon={<ArrowBack />}
                      onClick={handleBack}
                      variant="outlined"
                    >
                      Back
                    </Button>
                  )}
                  
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      sx={{
                        background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #FFA500 30%, #FFD700 90%)',
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} /> : (t?.createAccount || 'Create Account')}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForward />}
                      sx={{
                        background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #FFA500 30%, #FFD700 90%)',
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
